import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Aviso,
  Botao,
  BotaoLink,
  Campo,
  CampoArea,
  Carregando,
  Cartao,
  Etiqueta,
  Icone,
  Select,
  Selo,
  useDialogo,
} from '../../componentes/ui/index.ts';
import { api, data, ErroApi, moeda, telefone } from '../../lib/api.ts';
import {
  ROTULO_PAGAMENTO,
  type Lancamento,
  type LinhaFinanceira,
  type Saldo,
  type TipoPagamento,
} from '../../lib/tipos.ts';

type Resposta = {
  inscricao: Omit<LinhaFinanceira, 'pago' | 'saldo' | 'quitado'>;
  saldo: Saldo;
  extrato: Lancamento[];
};

const FORMAS = (Object.keys(ROTULO_PAGAMENTO) as TipoPagamento[]).map((t) => ({
  valor: t,
  rotulo: ROTULO_PAGAMENTO[t],
}));

export function FichaFinanceira() {
  const { id = '' } = useParams();
  const [ficha, setFicha] = useState<Resposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const { perguntar, dialogo } = useDialogo();

  const [tipo, setTipo] = useState<TipoPagamento>('pix');
  const [valor, setValor] = useState('');
  const [pagoEm, setPagoEm] = useState(() => new Date().toISOString().slice(0, 10));
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [editandoValor, setEditandoValor] = useState(false);
  const [novoValor, setNovoValor] = useState('');
  const [salvandoValor, setSalvandoValor] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setFicha(await api<Resposta>(`/api/financeiro/inscricoes/${id}`));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao carregar');
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function lancar(e: React.FormEvent) {
    e.preventDefault();
    setSalvando(true);
    setErro(null);
    try {
      await api(`/api/financeiro/inscricoes/${id}/pagamentos`, {
        metodo: 'POST',
        corpo: { tipo, valor, pagoEm, observacao: observacao || undefined },
      });
      setValor('');
      setObservacao('');
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao lançar');
    } finally {
      setSalvando(false);
    }
  }

  async function salvarValor() {
    setSalvandoValor(true);
    setErro(null);
    try {
      await api(`/api/financeiro/inscricoes/${id}/valor`, {
        metodo: 'PATCH',
        corpo: { valorTotal: novoValor },
      });
      setEditandoValor(false);
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao alterar o valor');
    } finally {
      setSalvandoValor(false);
    }
  }

  async function estornar(lancamentoId: string, motivo: string) {
    try {
      await api(`/api/financeiro/pagamentos/${lancamentoId}/estornar`, {
        metodo: 'POST',
        corpo: { observacao: motivo || undefined },
      });
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao estornar');
    }
  }

  if (carregando) return <Carregando />;
  if (!ficha) return <Aviso tipo="erro">{erro ?? 'Inscrição não encontrada.'}</Aviso>;

  const { inscricao, saldo, extrato } = ficha;
  const estornados = new Set(extrato.filter((l) => l.estornoDe).map((l) => l.estornoDe));

  return (
    <div className="pilha-6">
      {dialogo}

      <BotaoLink
        para="/painel/financeiro"
        variante="fantasma"
        tamanho="pequeno"
        icone={<Icone.Voltar tamanho={15} />}
      >
        Voltar ao financeiro
      </BotaoLink>

      <div className="linha linha-entre linha-topo">
        <div className="pilha-2">
          <Etiqueta>{inscricao.protocolo}</Etiqueta>
          <h2>{inscricao.nomeAdolescente}</h2>
          <div className="texto-suave texto-sm">
            {inscricao.nomeResponsavel ?? 'Responsável'} ·{' '}
            {telefone(inscricao.telefoneResponsavel)} · {inscricao.emailResponsavel}
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div className="texto-fraco">Saldo</div>
          <div
            className="numeral mono"
            style={{ color: saldo.quitado ? 'var(--sucesso)' : 'var(--laranja-texto)' }}
          >
            {saldo.quitado ? 'quitado' : moeda(saldo.saldo)}
          </div>
          {editandoValor ? (
            <div className="linha" style={{ gap: 'var(--gap-2)', marginTop: 'var(--gap-2)' }}>
              <Campo
                value={novoValor}
                onChange={(e) => setNovoValor(e.target.value)}
                inputMode="decimal"
                placeholder="150,00"
                prefixo="R$"
                style={{ maxWidth: '9rem' }}
              />
              <Botao tamanho="pequeno" carregando={salvandoValor} onClick={salvarValor}>
                Salvar
              </Botao>
              <Botao
                tamanho="pequeno"
                variante="fantasma"
                disabled={salvandoValor}
                onClick={() => setEditandoValor(false)}
              >
                Cancelar
              </Botao>
            </div>
          ) : (
            <div className="linha" style={{ gap: 'var(--gap-2)', justifyContent: 'flex-end' }}>
              <span className="texto-fraco">
                {moeda(saldo.pago)} de {moeda(saldo.valorTotal)}
              </span>
              <Botao
                tamanho="pequeno"
                variante="fantasma"
                onClick={() => {
                  setNovoValor(saldo.valorTotal);
                  setEditandoValor(true);
                }}
              >
                Editar
              </Botao>
            </div>
          )}
        </div>
      </div>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      <Cartao marcado titulo="Lançar pagamento">
        <form className="pilha" onSubmit={lancar}>
          <p className="campo__dica">
            Parcelas de valores diferentes são o normal no pix. Lance cada uma conforme entrar.
          </p>

          <div className="grade">
            <Select rotulo="Forma" opcoes={FORMAS} valor={tipo} aoMudar={setTipo} />

            <Campo
              rotulo="Valor"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              inputMode="decimal"
              placeholder="150,00"
              prefixo="R$"
              required
              obrigatorio
            />

            <Campo
              rotulo="Recebido em"
              type="date"
              value={pagoEm}
              onChange={(e) => setPagoEm(e.target.value)}
            />
          </div>

          <CampoArea
            rotulo="Observação"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            opcional
            maximo={500}
            dica="Ex: comprovante enviado no grupo."
          />

          <div className="linha linha-fim">
            <Botao type="submit" carregando={salvando} disabled={!valor}>
              Lançar pagamento
            </Botao>
          </div>
        </form>
      </Cartao>

      <section className="pilha">
        <h3>Extrato</h3>

        {extrato.length === 0 ? (
          <Cartao>
            <p className="texto-fraco" style={{ textAlign: 'center' }}>
              Nenhum lançamento ainda.
            </p>
          </Cartao>
        ) : (
          <div className="tabela-caixa">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Forma</th>
                  <th className="num">Valor</th>
                  <th>Observação</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {extrato.map((l) => {
                  const eEstorno = l.natureza === 'estorno';
                  const anulado = estornados.has(l.id);

                  return (
                    <tr key={l.id} style={anulado ? { opacity: 0.55 } : undefined}>
                      <td className="mono">{data(l.pagoEm)}</td>
                      <td>
                        <span className="linha" style={{ gap: 'var(--gap-2)' }}>
                          {ROTULO_PAGAMENTO[l.tipo]}
                          {eEstorno && <Selo tom="erro">estorno</Selo>}
                          {anulado && <Selo tom="neutro">estornado</Selo>}
                        </span>
                      </td>
                      <td
                        className="num mono"
                        style={eEstorno ? { color: 'var(--erro)' } : undefined}
                      >
                        {eEstorno ? '−' : ''}
                        {moeda(l.valor)}
                      </td>
                      <td className="texto-fraco">{l.observacao ?? '—'}</td>
                      <td>
                        {/* Estorno só faz sentido em lançamento ainda válido:
                            um estorno não se estorna, e nada é deletado. */}
                        {!eEstorno && !anulado && (
                          <Botao
                            variante="perigo"
                            tamanho="pequeno"
                            onClick={() =>
                              perguntar({
                                titulo: `Estornar ${moeda(l.valor)}?`,
                                texto:
                                  'O lançamento não é apagado: entra uma linha de estorno apontando para ele, e o saldo é recalculado.',
                                campo: { rotulo: 'Motivo', dica: 'Fica registrado no extrato.' },
                                confirmar: 'Estornar',
                                perigo: true,
                                aoConfirmar: (motivo) => estornar(l.id, motivo),
                              })
                            }
                          >
                            Estornar
                          </Botao>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
